import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";
import { AcademicDisclaimer } from "@/components/AcademicDisclaimer";
import { SiteHeader } from "@/components/SiteHeader";
import { DISCLAIMER_LEGAL, DISCLAIMER_FULL } from "@/lib/disclaimers";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service & Responsible AI Use — SmartEssay" },
      {
        name: "description",
        content:
          "SmartEssay.ai Terms of Service, Responsible AI Use, and Academic Integrity policy. SmartEssay is an assistive tool — users are responsible for the work they submit.",
      },
      { property: "og:title", content: "Terms of Service — SmartEssay" },
      {
        property: "og:description",
        content: "Terms of Service, Responsible AI Use, and Academic Integrity policy.",
      },
      { property: "og:url", content: "https://smartessay.ai/terms" },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/terms" }],
  }),
  component: TermsPage,
});

function Section({
  id,
  title,
  children,
}: {
  id?: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display text-2xl font-semibold tracking-tight">{title}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

function TermsPage() {
  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 pb-24">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-7 w-7 text-primary" />
          </div>
          <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            Terms of Service & Responsible AI Use
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>

        <div
          id="integrity"
          className="mt-10 scroll-mt-24 rounded-2xl border-2 border-primary/30 bg-primary/5 p-6 shadow-soft sm:p-8"
        >
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Academic Integrity & Responsible Use
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground">{DISCLAIMER_LEGAL}</p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{DISCLAIMER_FULL}</p>
        </div>

        <div className="mt-12 space-y-10">
          <Section title="1. Introduction">
            <p>
              These Terms of Service ("Terms") govern your use of SmartEssay.ai (the "Service"). By
              creating an account or using the Service, you agree to these Terms. If you do not
              agree, do not use the Service.
            </p>
          </Section>

          <Section title="2. What SmartEssay Is">
            <p>
              SmartEssay is an AI writing assistant for students and educators. The Service includes:
            </p>
            <ul className="ml-5 list-disc space-y-1">
              <li>
                Three generation modes — <strong>Essay</strong> (Claim-Evidence-Reasoning
                outlines and full sample drafts), <strong>Summary</strong> (structured
                summaries, including an APA 7 peer-reviewed article format with Introduction,
                Participants, Measurement Instrument, Results, and Critique sections), and{" "}
                <strong>Key Concepts</strong> (thematic maps with definitions).
              </li>
              <li>Real, verifiable quotes and citations in MLA or APA 7 format.</li>
              <li>
                Formatted PDF exports with proper heading, student title page, double-spaced
                body, and a Works Cited or References page. On-screen drafts use asterisks
                (<code>*like this*</code>) to mark italics; exported PDFs render them in true
                italics, and APA exports automatically italicize titles and journal names.
              </li>
              <li>Tone, reading level, evidences-per-claim, body-paragraph, and word-target controls; paraphrasing and polish tools.</li>
              <li>
                <strong>Essay Reviewer</strong> — paste or upload an existing draft (.txt, .md,
                .pdf, .docx) and get a structured report covering evidence gaps with real
                source quotes, a paragraph-by-paragraph flow map, and prioritized improvement
                ideas. Source material is optional.
              </li>
              <li>Voice dictation, drag-and-drop source uploads (.txt, .md, .pdf, .docx), and auto-saved history with search, filter, and bulk delete.</li>
              <li>
                <strong>Voice matching</strong>: optionally paste a writing sample or upload a
                voice or video recording so the AI mirrors your sentence length, vocabulary,
                contractions, and rhythm. You can save a permanent voice profile or override
                per essay.
              </li>
              <li>An anti-repetition guard that filters common AI-tell words and patterns on every generation, reviewer report, and tone rewrite.</li>
              <li>Account management, billing, and a free trial allotment before subscription is required.</li>
            </ul>
          </Section>

          <Section title="3. Voice Matching, Recordings & Likeness">
            <p>
              Voice matching is optional and off by default. By uploading a writing sample, voice
              recording, or video recording, you confirm that the content is yours (or that you
              have permission to use it) and you grant SmartEssay a limited license to transcribe
              it and extract style signals — sentence length, vocabulary tier, contractions,
              favorite connectors, and tone — solely to generate drafts in your style.
            </p>
            <p>
              We do not use voice or video samples for speaker identification, biometric
              authentication, advertising, or any purpose other than style matching for your own
              drafts. Original audio and video files are deleted after transcription unless you
              attach them to a saved voice profile. You can delete any saved profile, sample, or
              recording at any time from the My Voice settings.
            </p>
          </Section>


          <Section title="4. User Responsibilities">
            <p>You are solely responsible for:</p>
            <ul className="ml-5 list-disc space-y-1">
              <li>Reviewing, editing, and substantially revising any AI-generated content before submission.</li>
              <li>Verifying all quotes, citations, and sources against the original work.</li>
              <li>Disclosing AI assistance when required by your instructor, school, or publisher.</li>
              <li>Following your institution's academic integrity, plagiarism, and AI-use policies.</li>
              <li>Any consequences arising from misuse of generated material.</li>
            </ul>
          </Section>

          <Section title="5. Acceptable Use">
            <p>You agree not to use SmartEssay to:</p>
            <ul className="ml-5 list-disc space-y-1">
              <li>Submit AI-generated work as entirely your own where prohibited.</li>
              <li>Cheat on exams, certifications, or any assessment that disallows AI assistance.</li>
              <li>Generate unlawful, harassing, defamatory, or harmful content.</li>
              <li>Attempt to reverse engineer, scrape, or overload the Service.</li>
            </ul>
          </Section>

          <Section title="6. Accounts & Payment">
            <p>
              You must provide accurate information when creating an account and keep your
              credentials secure. Paid plans are billed according to the pricing displayed at
              checkout. Subscriptions renew automatically until canceled.
            </p>
          </Section>

          <Section title="7. Intellectual Property">
            <p>
              You retain ownership of the text you enter and of your final edited work. SmartEssay
              retains rights to the Service, its branding, and its underlying technology. You grant
              us a limited license to process your inputs solely to provide the Service.
            </p>
          </Section>

          <Section title="8. Disclaimers & Limitation of Liability">
            <p>
              The Service is provided "as is" without warranties of any kind. AI output may contain
              errors, omissions, or inaccuracies. To the maximum extent permitted by law,
              SmartEssay is not liable for any indirect, incidental, or consequential damages,
              including academic, disciplinary, or professional consequences arising from your use
              of generated content.
            </p>
          </Section>

          <Section id="privacy" title="9. Privacy">
            <p>
              We store the inputs and outputs associated with your account to provide history and
              improve quality. We do not sell personal data. For questions about your data or to
              request deletion, contact us via the link below.
            </p>
          </Section>

          <Section title="10. Changes to These Terms">
            <p>
              We may update these Terms from time to time. Material changes will be communicated
              through the Service. Continued use after changes constitutes acceptance.
            </p>
          </Section>

          <Section title="11. Age Requirement">
            <p>
              SmartEssay is intended for users 13 and older. Users ages 13–17 must have
              parental or guardian consent and should use the service in accordance with their school's
              policies. Users under 13 are not permitted to create an account or use the Service.
            </p>
          </Section>

          <Section title="12. Refunds">
            <p>
              We offer a 30-day money-back guarantee on annual plans. Monthly plans can be
              canceled anytime — you keep access through the end of the billing period.
            </p>
          </Section>

          <Section title="13. School & Bulk Licensing">
            <p>
              We offer FERPA-compliant licensing for schools and districts, including signed DPAs
              and volume pricing. Email{" "}
              <a
                href="mailto:support@tilt.university"
                className="text-primary underline-offset-2 hover:underline"
              >
                support@tilt.university
              </a>{" "}
              to start a conversation.
            </p>
          </Section>

          <Section title="14. Contact">
            <p>
              Questions about these Terms? Reach us through the{" "}
              <Link to="/contact" className="text-primary underline-offset-2 hover:underline">
                contact page
              </Link>
              .
            </p>
          </Section>
        </div>

        <div className="mt-14">
          <AcademicDisclaimer variant="inline" />
        </div>
      </main>

    </div>
  );
}
